(() => {
  'use strict';

  const HERO_SLOT_CLASSES = [
    'commerce-hero__fan-card--1',
    'commerce-hero__fan-card--2',
    'commerce-hero__fan-card--3'
  ];
  const HERO_PROTECTED_CATEGORIES = ['index', 'objet-magique'];

  function randomIndex(length) {
    if (length <= 1) return 0;

    try {
      if (window.crypto?.getRandomValues) {
        const buffer = new Uint32Array(1);
        window.crypto.getRandomValues(buffer);
        return buffer[0] % length;
      }
    } catch (_) {
      // Math.random remains a safe visual fallback.
    }

    return Math.floor(Math.random() * length);
  }

  function shuffle(values) {
    const shuffled = values.slice();

    for (let index = shuffled.length - 1; index > 0; index -= 1) {
      const other = randomIndex(index + 1);
      [shuffled[index], shuffled[other]] = [shuffled[other], shuffled[index]];
    }

    return shuffled;
  }

  function productHandle(element) {
    return element?.dataset?.productHandle || '';
  }

  function productCategory(element) {
    return element?.dataset?.productCategory || '';
  }

  function visibleHandles(order, count) {
    return order.slice(0, count).map(productHandle).filter(Boolean);
  }

  function sameVisibleSelection(current, previous, compareAsSet) {
    if (!Array.isArray(previous) || current.length !== previous.length) return false;

    if (compareAsSet) {
      return current.slice().sort().join('|') === previous.slice().sort().join('|');
    }

    return current.every((handle, index) => handle === previous[index]);
  }

  function sameSet(left, right) {
    if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) return false;
    return left.slice().sort().join('|') === right.slice().sort().join('|');
  }

  function readStoredObject(key, fallback = {}) {
    try {
      const parsed = JSON.parse(window.sessionStorage.getItem(key) || 'null');
      return parsed && typeof parsed === 'object' ? parsed : fallback;
    } catch (_) {
      return fallback;
    }
  }

  function writeStoredObject(key, value) {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
    } catch (_) {
      // Rotation still works when storage is unavailable.
    }
  }

  function readPrevious(key) {
    const parsed = readStoredObject(key);
    return Array.isArray(parsed.visible) ? parsed.visible : [];
  }

  function storeSelection(key, order, visibleCount) {
    writeStoredObject(key, {
      visible: visibleHandles(order, visibleCount)
    });
  }

  function deterministicAlternative(candidates, count, previous) {
    if (candidates.length <= 1) return candidates.slice();

    const previousFirst = previous[0];
    const previousIndex = candidates.findIndex((candidate) => productHandle(candidate) === previousFirst);
    const start = previousIndex >= 0 ? (previousIndex + 1) % candidates.length : 1;
    const rotated = candidates.slice(start).concat(candidates.slice(0, start));

    if (candidates.length === count && sameVisibleSelection(
      visibleHandles(rotated, count),
      previous,
      false
    )) {
      return candidates.slice(1).concat(candidates.slice(0, 1));
    }

    return rotated;
  }

  function chooseOrder(candidates, visibleCount, key) {
    const count = Math.min(visibleCount, candidates.length);
    const previous = readPrevious(key);
    const compareAsSet = candidates.length > count;
    let order = candidates.slice();

    for (let attempt = 0; attempt < 18; attempt += 1) {
      order = shuffle(candidates);
      if (!sameVisibleSelection(visibleHandles(order, count), previous, compareAsSet)) break;
    }

    if (sameVisibleSelection(visibleHandles(order, count), previous, compareAsSet)) {
      order = deterministicAlternative(candidates, count, previous);
    }

    storeSelection(key, order, count);
    return order;
  }

  function chooseBalancedHero(candidates, visibleCount, key) {
    const groups = new Map();

    candidates.forEach((candidate) => {
      const category = productCategory(candidate);
      if (!category) return;
      if (!groups.has(category)) groups.set(category, []);
      groups.get(category).push(candidate);
    });

    const categories = Array.from(groups.keys());
    const count = Math.min(visibleCount, categories.length);
    if (!count) return [];

    const stateKey = `${key}:category-state`;
    const state = readStoredObject(stateKey, { counts: {}, previousCategories: [] });
    const counts = {};
    categories.forEach((category) => {
      counts[category] = Math.max(0, Number(state.counts?.[category] || 0));
    });

    const randomRanks = new Map(shuffle(categories).map((category, index) => [category, index]));
    const byLowestUsage = (left, right) => (
      counts[left] - counts[right]
      || randomRanks.get(left) - randomRanks.get(right)
    );

    const protectedCategory = HERO_PROTECTED_CATEGORIES
      .filter((category) => groups.has(category))
      .sort(byLowestUsage)[0] || null;

    const selectedCategories = [];
    if (protectedCategory) selectedCategories.push(protectedCategory);

    categories
      .filter((category) => category !== protectedCategory)
      .sort(byLowestUsage)
      .forEach((category) => {
        if (selectedCategories.length < count) selectedCategories.push(category);
      });

    const previousCategories = Array.isArray(state.previousCategories) ? state.previousCategories : [];
    if (sameSet(selectedCategories, previousCategories) && categories.length > count) {
      const alternatives = categories
        .filter((category) => !selectedCategories.includes(category))
        .sort(byLowestUsage);
      const replaceableIndexes = selectedCategories
        .map((category, index) => ({ category, index }))
        .filter(({ category }) => category !== protectedCategory)
        .sort((left, right) => counts[right.category] - counts[left.category]);

      if (alternatives.length && replaceableIndexes.length) {
        selectedCategories[replaceableIndexes[0].index] = alternatives[0];
      }
    }

    let orderedCategories;
    if (protectedCategory && selectedCategories.includes(protectedCategory)) {
      orderedCategories = shuffle(selectedCategories.filter((category) => category !== protectedCategory));
      const protectedPosition = randomIndex(Math.min(2, count));
      orderedCategories.splice(protectedPosition, 0, protectedCategory);
    } else {
      orderedCategories = shuffle(selectedCategories);
    }

    const selectedProducts = orderedCategories
      .map((category) => chooseOrder(groups.get(category), 1, `${key}:product:${category}`)[0])
      .filter(Boolean);

    selectedCategories.forEach((category) => {
      counts[category] = (counts[category] || 0) + 1;
    });

    const minimumCount = Math.min(...categories.map((category) => counts[category] || 0));
    if (minimumCount > 0) {
      categories.forEach((category) => {
        counts[category] -= minimumCount;
      });
    }

    writeStoredObject(stateKey, {
      counts,
      previousCategories: selectedCategories.slice()
    });

    return selectedProducts;
  }

  function resetElement(element) {
    element.classList.remove('is-selected');
    element.hidden = true;
    element.removeAttribute('data-rotation-slot');
  }

  function applySelectedElements(elements, handles, count) {
    const byHandle = new Map(elements.map((element) => [productHandle(element), element]));
    elements.forEach(resetElement);

    handles.slice(0, count).forEach((handle, index) => {
      const element = byHandle.get(handle);
      if (!element) return;
      element.hidden = false;
      element.classList.add('is-selected');
      element.dataset.rotationSlot = String(index + 1);
    });
  }

  function applyHeroDesktop(elements, handles) {
    const byHandle = new Map(elements.map((element) => [productHandle(element), element]));

    elements.forEach((element) => {
      resetElement(element);
      HERO_SLOT_CLASSES.forEach((className) => element.classList.remove(className));
    });

    handles.slice(0, 3).forEach((handle, index) => {
      const element = byHandle.get(handle);
      if (!element) return;
      element.hidden = false;
      element.classList.add('is-selected', HERO_SLOT_CLASSES[index]);
      element.dataset.rotationSlot = String(index + 1);
    });
  }

  function initializeHero(root) {
    if (!root || root.dataset.rotationReady === 'true') return;

    const desktop = Array.from(root.querySelectorAll('[data-hero-desktop] [data-rotation-candidate]'));
    const mobile = Array.from(root.querySelectorAll('[data-hero-mobile] [data-rotation-candidate]'));
    if (!desktop.length) return;

    root.dataset.rotationReady = 'true';

    const key = `lcdd:hero:v2:${window.location.pathname}:${root.dataset.rotationKey || root.id || 'default'}`;
    const selected = chooseBalancedHero(desktop, 3, key);
    const handles = selected.map(productHandle).filter(Boolean);

    applyHeroDesktop(desktop, handles);

    const applyMobile = () => {
      const count = window.matchMedia('(max-width: 620px)').matches ? 2 : 3;
      applySelectedElements(mobile, handles, count);
    };

    applyMobile();

    const media = window.matchMedia('(max-width: 620px)');
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', applyMobile);
    } else if (typeof media.addListener === 'function') {
      media.addListener(applyMobile);
    }
  }

  function initializeMaillage(root) {
    if (!root || root.dataset.rotationReady === 'true') return;

    const candidates = Array.from(root.querySelectorAll('[data-maillage-candidate]'));
    if (!candidates.length) return;

    root.dataset.rotationReady = 'true';

    const initialVisibleCount = window.matchMedia('(max-width: 980px)').matches ? 2 : 4;
    const key = `lcdd:maillage:${window.location.pathname}:${root.dataset.rotationKey || root.id || 'default'}`;
    const order = chooseOrder(candidates, initialVisibleCount, key);
    const handles = order.map(productHandle).filter(Boolean);

    const apply = () => {
      const count = window.matchMedia('(max-width: 980px)').matches ? 2 : 4;
      applySelectedElements(candidates, handles, count);
    };

    apply();

    const media = window.matchMedia('(max-width: 980px)');
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', apply);
    } else if (typeof media.addListener === 'function') {
      media.addListener(apply);
    }
  }

  function initializeCartRecommendation(root) {
    if (!root || root.dataset.rotationReady === 'true') return;

    const candidates = Array.from(root.querySelectorAll('[data-cart-recommendation-candidate]'));
    if (!candidates.length) return;

    root.dataset.rotationReady = 'true';

    const visibleCount = Math.max(1, Number.parseInt(root.dataset.visibleCount || '1', 10));
    const key = `lcdd:cart-recommendation:${window.location.pathname}:${root.dataset.rotationKey || 'default'}`;
    const order = chooseOrder(candidates, visibleCount, key);
    const handles = order.map(productHandle).filter(Boolean);

    applySelectedElements(candidates, handles, visibleCount);
  }

  function initialize(scope = document) {
    scope.querySelectorAll?.('[data-hero-rotation]').forEach(initializeHero);
    scope.querySelectorAll?.('[data-maillage-rotation]').forEach(initializeMaillage);
    scope.querySelectorAll?.('[data-cart-recommendation-rotation]').forEach(initializeCartRecommendation);

    if (scope.matches?.('[data-hero-rotation]')) initializeHero(scope);
    if (scope.matches?.('[data-maillage-rotation]')) initializeMaillage(scope);
    if (scope.matches?.('[data-cart-recommendation-rotation]')) initializeCartRecommendation(scope);
  }

  initialize();

  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
  });

  if (document.body && typeof MutationObserver === 'function') {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) initialize(node);
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }
})();
